/**
 * Overtime API client. Wraps /v1/attendance/overtime/* — the two-stage
 * approval surface (ADR-0014 D-4 v0.3): Stage 1 line manager → Stage 2 HR Admin.
 */
import { apiRequest } from './client.js';

export type OvertimeStatus =
  | 'computed'
  | 'pending_manager'
  | 'pending_hr_admin'
  | 'approved'
  | 'rejected';

export interface OvertimeItem {
  id: string;
  employeeId: string;
  /** Denormalised on the list endpoint so the queue can show who it's for. */
  employee?: { firstName: string; lastName: string; employeeNumber: string } | null;
  periodStart: string;
  periodEnd: string;
  overtimeHours: string;
  hourlyRate: string;
  multiplier: string;
  computedAmount: string;
  currency: string;
  status: OvertimeStatus;
  approverUserId: string | null;
}

export function listOvertime(
  q: { view?: 'pending_my_approval'; status?: OvertimeStatus; employeeId?: string } = {},
): Promise<{ items: OvertimeItem[] }> {
  const params = new URLSearchParams();
  if (q.view) params.set('view', q.view);
  if (q.status) params.set('status', q.status);
  if (q.employeeId) params.set('employeeId', q.employeeId);
  const qs = params.toString();
  return apiRequest<{ items: OvertimeItem[] }>(`/v1/attendance/overtime${qs ? `?${qs}` : ''}`);
}

/** Stage 1 — line manager approves (pending_manager → pending_hr_admin). No pay. */
export function managerApproveOvertime(id: string, body: { reason?: string } = {}): Promise<unknown> {
  return apiRequest(`/v1/attendance/overtime/${encodeURIComponent(id)}/manager-approve`, { method: 'POST', body });
}

/** Stage 2 — HR Admin (admin/owner) final approval, mints the pay. */
export function approveOvertime(id: string, body: { payDate?: string; reason?: string } = {}): Promise<unknown> {
  return apiRequest(`/v1/attendance/overtime/${encodeURIComponent(id)}/approve`, { method: 'POST', body });
}

/** Reject at either stage — kills the item, never mints pay. */
export function rejectOvertime(id: string, body: { reason?: string } = {}): Promise<unknown> {
  return apiRequest(`/v1/attendance/overtime/${encodeURIComponent(id)}/reject`, { method: 'POST', body });
}
